/**
 * Age step: the 18+ date-of-birth question, asked before the username. Presentational — the
 * route owns the age-gate status and the submit. Reuses the coin-compliance prompt so the copy,
 * validation and confirm-before-save stay identical to the global gate.
 * See specs/onboarding-questionnaire/spec.md (Story 7).
 */

import { ScrollView, Text, View } from 'react-native';
import { AnimatedMonkey } from '../../AnimatedMonkey/AnimatedMonkey';
import { AgeVerificationPrompt } from '../../../../src/ageGate/components/AgeVerificationPrompt/AgeVerificationPrompt';
import type { AgeGateSubmitError } from '../../../../src/ageGate/hooks/useAgeVerification';
import type { DateOfBirthParts } from '../../../../src/ageGate/helpers/dateOfBirth.helper';
import { onboardingStyles as s } from '../OnboardingFlow.styles';
import { ONBOARDING_COPY, ONBOARDING_TEST_IDS } from '../OnboardingFlow.constants';
import { BackButton } from './BackButton';

interface AgeStepProps {
  blocked: boolean;
  isSubmitting: boolean;
  submitError: AgeGateSubmitError;
  onSubmit: (parts: DateOfBirthParts) => void;
  onSignOut: () => void;
  onBack: () => void;
  progress: number;
}

export function AgeStep({
  blocked, isSubmitting, submitError, onSubmit, onSignOut, onBack, progress,
}: AgeStepProps) {
  return (
    <View style={s.ageScroll} testID={ONBOARDING_TEST_IDS.AGE_STEP}>
      {/* Blocked is a dead end: no Back and no progress, only sign out (AC-20). */}
      <View style={s.topRow}>
        {blocked ? null : (
          <>
            <BackButton onPress={onBack} />
            <View style={s.progressTrack}>
              <View style={[s.progressFill, { width: `${Math.round(progress * 100)}%` }]} />
            </View>
          </>
        )}
      </View>

      {blocked ? null : (
        <View style={s.questionHeader}>
          <AnimatedMonkey size={72} />
          <View style={s.smallBubble}>
            <Text style={s.smallBubbleText}>{ONBOARDING_COPY.agePrompt}</Text>
          </View>
        </View>
      )}

      <ScrollView
        style={s.ageScroll}
        contentContainerStyle={s.ageScrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <AgeVerificationPrompt
          mode={blocked ? 'blocked' : 'prompt'}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onSubmit={onSubmit}
          onSignOut={onSignOut}
        />
      </ScrollView>
    </View>
  );
}
